import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Drawer,
  Form,
  Input,
  Button,
  Upload,
  Switch,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { PlusOutlined, PictureOutlined } from "@ant-design/icons";
import { addBanner, updateBanner, getBanners } from "../../../api/banner/banner";
import { resolveImageUrl } from "../../../utils/image";
import {
  beforeUpload,
  compressImage,
  getBase64,
} from "../../../utils/imageUpload";
import { neutral, spacing, border } from "../../../theme/tokens";
import {
  AUDIENCE_OPTIONS,
  BANNER_RATIO,
  BANNER_RATIO_TOLERANCE,
  BANNER_SIZE_HINT,
  targetOptions,
} from "./bannerConstants";

const { Text } = Typography;

// Khung preview = tỉ lệ mặc định của BannerCarousel bên mini app (ảnh 1080×540).
const PREVIEW_RATIO = "2 / 1";

export default function BannerFormDrawer({
  open,
  mode = "add",
  record,
  currentPage,
  pageSize,
  onClose,
}) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(""); // ảnh hiển thị ở khung preview sống
  const [loading, setLoading] = useState(false);

  const isEdit = mode === "edit";

  /*
   * Điểm đến phụ thuộc đối tượng: "Tất cả" chỉ có những màn tồn tại ở CẢ hai nhánh vai
   * (xem bannerConstants). Đổi đối tượng mà điểm đến đang chọn không còn hợp lệ thì xóa
   * luôn — để nguyên là BE trả 400 lúc lưu, người dùng không hiểu vì sao.
   */
  const audience = Form.useWatch("audience", form);
  const targets = targetOptions(audience);

  useEffect(() => {
    const current = form.getFieldValue("link_url");
    if (current && !targets.some((t) => t.value === current)) {
      form.setFieldValue("link_url", undefined);
    }
  }, [audience, targets, form]);

  // Nạp dữ liệu khi mở drawer (add: reset, edit: đổ record).
  useEffect(() => {
    if (!open) return;
    if (isEdit && record) {
      form.setFieldsValue({
        title: record.title,
        audience: record.audience || "ALL",
        link_url: record.link_url || undefined,
        status: record.status === true,
      });
      if (record.image_path) {
        const url = resolveImageUrl(record.image_path);
        setFileList([{ uid: "-1", name: "current", status: "done", url }]);
        setPreviewUrl(url);
      } else {
        setFileList([]);
        setPreviewUrl("");
      }
    } else {
      form.resetFields();
      form.setFieldsValue({ status: true, audience: "ALL" });
      setFileList([]);
      setPreviewUrl("");
    }
  }, [open, isEdit, record, form]);

  /*
   * Mini app render ảnh bằng `object-cover` theo khung 2:1: ảnh lệch tỉ lệ KHÔNG lỗi
   * nhưng bị cắt hai đầu, và trên app mới phát hiện. Nhắc ngay lúc chọn ảnh, vẫn cho lưu.
   */
  const warnIfOffRatio = (dataUrl) => {
    const probe = document.createElement("img");
    probe.onload = () => {
      if (!probe.naturalWidth || !probe.naturalHeight) return;
      const ratio = probe.naturalWidth / probe.naturalHeight;
      if (Math.abs(ratio - BANNER_RATIO) / BANNER_RATIO > BANNER_RATIO_TOLERANCE) {
        message.warning(
          `Ảnh ${probe.naturalWidth}×${probe.naturalHeight} lệch tỉ lệ 2:1 — mini app sẽ cắt bớt hai đầu. Khuyến nghị ${BANNER_SIZE_HINT}.`
        );
      }
    };
    probe.src = dataUrl;
  };

  const handleChange = async ({ fileList: next }) => {
    setFileList(next);
    const file = next[0];
    if (!file) {
      setPreviewUrl("");
      return;
    }
    if (file.originFileObj) {
      try {
        const dataUrl = await getBase64(file.originFileObj);
        setPreviewUrl(dataUrl);
        warnIfOffRatio(dataUrl);
      } catch {
        setPreviewUrl(file.url || "");
      }
    } else {
      setPreviewUrl(file.url || "");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const raw0 = fileList[0]?.originFileObj;
      // Thêm mới bắt buộc có ảnh; sửa thì cho giữ ảnh cũ.
      if (!isEdit && !raw0) {
        message.error("Vui lòng tải lên hình ảnh banner!");
        return;
      }

      setLoading(true);

      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("audience", values.audience || "ALL");
      formData.append("status", values.status ? 1 : 0);

      // Không chọn điểm đến -> gửi rỗng để BE ghi null (banner chỉ để xem).
      formData.append("link_url", values.link_url || "");

      if (raw0) {
        formData.append("image", await compressImage(raw0)); // nén trước khi gửi
      }

      if (isEdit) {
        await dispatch(updateBanner({ id: record.id, data: formData })).unwrap();
        message.success("Cập nhật banner thành công");
      } else {
        await dispatch(addBanner(formData)).unwrap();
        message.success("Thêm mới banner thành công");
      }

      dispatch(getBanners({ currentPage, pageSize }));
      onClose?.();
    } catch (error) {
      if (error?.errorFields) return; // lỗi validate form -> AntD tự hiện
      message.error(isEdit ? "Cập nhật banner thất bại" : "Thêm mới banner thất bại");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={isEdit ? "Chỉnh sửa banner" : "Thêm mới banner"}
      placement="right"
      width="min(560px, 100vw)"
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{ body: { background: neutral[50], padding: spacing[6] } }}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
          <Button onClick={onClose}>Hủy</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            {isEdit ? "Cập nhật" : "Thêm mới"}
          </Button>
        </div>
      }
    >
      {/* Preview sống theo tỉ lệ banner */}
      <div
        style={{
          width: "100%",
          aspectRatio: PREVIEW_RATIO,
          borderRadius: 6,
          overflow: "hidden",
          background: neutral[100],
          border: border.hairline,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: spacing[5],
        }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="preview"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Space direction="vertical" align="center" size={4}>
            <PictureOutlined style={{ fontSize: 28, color: neutral[400] }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Xem trước banner ({BANNER_SIZE_HINT})
            </Text>
          </Space>
        )}
      </div>

      <Form form={form} layout="vertical" initialValues={{ status: true, audience: "ALL" }}>
        <Form.Item
          name="title"
          label="Tiêu đề"
          extra="Cũng là chữ hiển thị thay ảnh khi khách mất mạng."
          rules={[{ required: true, message: "Vui lòng nhập tiêu đề banner!" }]}
        >
          <Input placeholder="Nhập tiêu đề banner" maxLength={191} />
        </Form.Item>

        <Form.Item
          name="audience"
          label="Hiển thị cho"
          rules={[{ required: true, message: "Vui lòng chọn đối tượng hiển thị!" }]}
        >
          <Select options={AUDIENCE_OPTIONS} />
        </Form.Item>

        <Form.Item
          name="link_url"
          label="Khi bấm vào banner"
          extra={
            audience === "ALL"
              ? "Chọn “Tất cả” nên chỉ còn các màn có ở cả hai vai. Để trống = banner chỉ để xem."
              : "Để trống = banner chỉ để xem, không bấm được."
          }
        >
          <Select
            allowClear
            placeholder="Không điều hướng"
            options={targets}
          />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Hình ảnh{" "}
              <Text type="secondary" style={{ fontWeight: 400 }}>
                (khuyến nghị {BANNER_SIZE_HINT})
              </Text>
            </span>
          }
          required={!isEdit}
        >
          <Upload
            listType="picture-card"
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={handleChange}
            maxCount={1}
            accept="image/*"
            showUploadList={{ showPreviewIcon: false }}
          >
            {fileList.length >= 1 ? null : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Tải lên</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item name="status" label="Trạng thái" valuePropName="checked">
          <Switch checkedChildren="Hiển thị" unCheckedChildren="Ẩn" />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
